import { Badge, Text } from "@mantine/core";
import { format } from "date-fns";
import { Building2, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import MarkdownRenderer from "../../../../components/agno/MarkdownRenderer";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../../../components/ui/sheet";
import { cn } from "../../../../lib/utils";
import DocumentFeedbackControls from "../../signals/signal-documents/DocumentFeedbackControls";
import type { GroupedAccount, HealthStatusStyles } from "../healthTypes";
import { extractHealthInfo } from "../healthTypes";
import type { SignalDocument } from "../../../../services/signalDocuments/signalDocumentsEndpoints.interface";

type HealthDetailSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: GroupedAccount | null;
  statusStyles: HealthStatusStyles;
  workspaceId: string;
};

const HealthDetailSheet = ({
  open,
  onOpenChange,
  account,
  statusStyles,
  workspaceId,
}: HealthDetailSheetProps) => {
  const [showOlderReports, setShowOlderReports] = useState(false);

  if (!account) return null;

  const latestDoc = account.documents[0];
  const olderDocs = account.documents.slice(1);
  const style = account.latestStatus ? statusStyles[account.latestStatus] : null;
  const structured = latestDoc ? extractHealthInfo(latestDoc) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <SheetTitle className="text-base">{account.accountName}</SheetTitle>
          </div>
          <SheetDescription className="flex items-center gap-2">
            {style && account.latestStatus && (
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize",
                  style.badge,
                )}
              >
                <span className={cn("h-1.5 w-1.5 rounded-full", style.dot)} />
                {account.latestStatus.replace(/_/g, " ")}
              </span>
            )}
            {account.latestDate && (
              <span className="text-xs text-muted-foreground">
                Last assessed {format(new Date(account.latestDate), "MMM d, yyyy h:mm a")}
              </span>
            )}
          </SheetDescription>
        </SheetHeader>

        {/* Account link */}
        <Link
          to={`/home/${workspaceId}/account/${account.entityId}/overview`}
          className="mb-4 inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          View account detail <ExternalLink className="h-3 w-3" />
        </Link>

        {/* Latest document */}
        {latestDoc && (
          <div className="space-y-4">
            {/* Summary */}
            {structured?.summary && (
              <p className="text-sm italic text-muted-foreground">{structured.summary}</p>
            )}

            {/* Model info badges */}
            <div className="flex flex-wrap items-center gap-1.5">
              {latestDoc.health_model_label && (
                <Badge variant="outline" size="xs">
                  {latestDoc.health_model_label}
                </Badge>
              )}
              {latestDoc.doc_version && (
                <Badge variant="outline" size="xs">
                  v{latestDoc.doc_version}
                </Badge>
              )}
              {(latestDoc.health_model_source_label || latestDoc.health_model_scope_value) && (
                <Badge variant="outline" size="xs">
                  {latestDoc.health_model_source_label || "Health model"}
                  {latestDoc.health_model_scope_type !== "general" && latestDoc.health_model_scope_value
                    ? ` \u00b7 ${latestDoc.health_model_scope_value}`
                    : ""}
                </Badge>
              )}
            </div>

            {/* Markdown content */}
            <div className="rounded-lg border border-border bg-card p-4">
              <MarkdownRenderer>
                {latestDoc.content_markdown || "_No analysis generated._"}
              </MarkdownRenderer>
            </div>

            {/* Feedback controls */}
            <div className="flex items-center justify-between">
              <DocumentFeedbackControls documentId={latestDoc.id} workspaceId={workspaceId} />
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                {typeof latestDoc.tokens_used === "number" && (
                  <span>{latestDoc.tokens_used} tokens</span>
                )}
                {typeof latestDoc.generation_time_ms === "number" && (
                  <span>{latestDoc.generation_time_ms}ms</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Older reports */}
        {olderDocs.length > 0 && (
          <div className="mt-6 border-t border-border pt-4">
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={() => setShowOlderReports(!showOlderReports)}
            >
              {showOlderReports
                ? "Hide older reports"
                : `View older reports (${olderDocs.length})`}
            </button>

            {showOlderReports && (
              <div className="mt-3 space-y-3">
                {olderDocs.map((doc) => (
                  <OlderReportCard key={doc.id} doc={doc} workspaceId={workspaceId} statusStyles={statusStyles} />
                ))}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const OlderReportCard = ({
  doc,
  workspaceId,
  statusStyles,
}: {
  doc: SignalDocument;
  workspaceId: string;
  statusStyles: HealthStatusStyles;
}) => {
  const structured = extractHealthInfo(doc);
  const healthStatus = typeof structured?.health_status === "string" ? structured.health_status : null;
  const style = healthStatus ? statusStyles[healthStatus] : null;
  const timestamp = doc.created_at
    ? format(new Date(doc.created_at), "MMM d, yyyy h:mm a")
    : "";

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {style && healthStatus && (
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium capitalize",
                style.badge,
              )}
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", style.dot)} />
              {healthStatus.replace(/_/g, " ")}
            </span>
          )}
          {doc.doc_version && (
            <Badge variant="outline" size="xs">v{doc.doc_version}</Badge>
          )}
        </div>
        <Text size="xs" c="dimmed">{timestamp}</Text>
      </div>

      {structured?.summary && (
        <p className="mb-2 text-xs italic text-muted-foreground">{structured.summary}</p>
      )}

      <MarkdownRenderer>
        {doc.content_markdown || "_No analysis generated._"}
      </MarkdownRenderer>

      <div className="mt-2 border-t border-border pt-2">
        <DocumentFeedbackControls documentId={doc.id} workspaceId={workspaceId} />
      </div>
    </div>
  );
};

export default HealthDetailSheet;
